import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { login } from "~/api/auth";
import { errorToast } from "~/components";
import { ROUTES } from "~/router";
import { useUserStore } from "~/stores";
import { Button, ErrorLabel, Input, InvizaLogo } from "~/ui";

export const loginSchema = z.object({
  email: z.string().min(1, { message: "Please enter your email" }),
  password: z.string().min(1, { message: "Please enter your password" }),
});

export const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setAuth = useUserStore((s) => s.setAuth);
  const setAdmin = useUserStore((s) => s.setAdmin);

  type LoginFormData = z.infer<typeof loginSchema>;

  const {
    handleSubmit,
    register,
    setError,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const { mutate } = useMutation({
    mutationFn: login,
    onSuccess: (data) => {
      setAuth(data);
      setAdmin(data.administrator);
      navigate(ROUTES.patients);
    },
    onError: (err) => {
      if (err instanceof AxiosError && err.response?.status === 401) {
        setError("root.invalidCredentials", { type: "401" });
      } else {
        errorToast(err);
      }
    },
  });

  const onSubmit: SubmitHandler<LoginFormData> = (data) => {
    mutate(data);
  };

  return (
    <main className="flex h-screen w-full flex-col text-primary lg:flex-row">
      <div className="flex h-full w-full flex-col items-center justify-center gap-6 bg-surface-container-high-dark p-8 lg:gap-14 lg:p-16">
        <div>
          <InvizaLogo className="h-inviza-logo-sm w-inviza-logo-sm lg:h-inviza-logo-lg lg:w-inviza-logo-lg" />
        </div>
        <p className="text-center text-xl lg:text-3xl">
          <span className="font-bold">INVIZA</span>
          <span className="align-top text-base">® </span>
          <span className="italic">Health</span>
        </p>
      </div>
      <div className="flex h-full w-full flex-col items-center justify-center gap-6 bg-background-primary-dark p-8 lg:p-16">
        <h1 className="w-full max-w-form text-2xl">
          {t("login.welcome_to_inviza")}
        </h1>
        <form
          className="flex max-w-form flex-col items-start justify-center gap-4"
          onSubmit={handleSubmit(onSubmit)}
        >
          <h2 className="text-xl">{t("login.login")}</h2>
          <Input
            id="email"
            label={t("login.email")}
            placeholder={t("login.enter_email")}
            error={errors.email?.message}
            {...register("email")}
          />
          <Input
            id="password"
            label={t("login.password")}
            placeholder={t("login.enter_password")}
            type="password"
            className="mt-2"
            error={errors.password?.message}
            {...register("password")}
          />
          {errors.root?.invalidCredentials && (
            <ErrorLabel className="text-sm">
              {t("login.invalid_credentials")}
            </ErrorLabel>
          )}
          <Button
            color="primary"
            size="base"
            type="submit"
            className="mt-6 w-full"
            text={t("login.login")}
          />
        </form>
      </div>
    </main>
  );
};
